import clsx from "clsx";
import { type ChangeEvent, type KeyboardEvent } from "react";
import { Divider, Icon, LoadingSpinner } from "@/components/common";
import { normalizeForSearch } from "@/utils";
import { TalentSearchCard } from "./TalentSearchCard";

export type PredictiveSearchResult = {
  id: string;
  imageUrl?: string | null;
  imageAlt: string;
  title: string;
  subtitle?: string | null;
  href: string;
};

type Props = {
  value: string;
  onValueChange: (value: string) => void;
  onSubmit?: (value: string) => void;
  isDropdownOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  results: PredictiveSearchResult[];
  isLoading: boolean;
  disabled?: boolean;
};

export const InputSearchListTalent = ({
  value,
  onValueChange,
  onSubmit,
  isDropdownOpen,
  onOpen,
  onClose,
  results,
  isLoading,
  disabled,
}: Props) => {
  const normalizedValue = normalizeForSearch(value);
  const hasValidQuantityLetters = normalizedValue.length > 2;
  const hasResults = hasValidQuantityLetters && results.length > 0;

  const handleValueChange = ({ target }: ChangeEvent<HTMLInputElement>) => {
    onValueChange(target.value);
  };

  const handleSubmit = () => {
    onSubmit?.(value);
    onClose();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSubmit();
    }
  };

  return (
    <>
      <div className={clsx("relative w-full lg:w-[392px]", { "z-[110]": isDropdownOpen })}>
        <div className="relative flex w-full items-center justify-between overflow-hidden border border-input-border bg-background-primary max-w-[392px]">
          <input
            type="text"
            value={value}
            placeholder="Buscar por nome ou código do talento"
            className="input w-full border-none bg-background-primary p-2"
            disabled={disabled}
            onChange={handleValueChange}
            onFocus={onOpen}
            onKeyDown={handleKeyDown}
          />
          <button
            type="button"
            aria-label="Buscar talento"
            className="mr-2 text-primary"
            disabled={disabled}
            onClick={handleSubmit}
          >
            <Icon name="Search" size="1.5rem" />
          </button>
        </div>

        <div
          className={clsx(
            "noscrollbar absolute left-0 right-0 top-[calc(100%_+_.5rem)] max-h-64 w-full gap-4 border border-input-border bg-white p-4 max-w-[392px]",
            {
              hidden: !isDropdownOpen,
            },
          )}
        >
          {isLoading && hasValidQuantityLetters && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/80 rounded">
              <LoadingSpinner size={24} />
            </div>
          )}

          {!hasValidQuantityLetters && (
            <span className="flex flex-1 justify-center font-normal text-xs">
              Digite o nome completo (ou parte dele) ou o código do talento. A busca ignora acentos e espaços extras.
            </span>
          )}

          {!isLoading && hasValidQuantityLetters && !hasResults && (
            <span className="flex flex-1 font-normal text-xs">Nenhum resultado encontrado.</span>
          )}

          {hasResults &&
            results.map((result, index) => (
              <div key={result.id} className="flex flex-col gap-4">
                <TalentSearchCard result={result} />
                {index < results.length - 1 && <Divider className="!mt-0 sm:!mt-0 !mb-4" />}
              </div>
            ))}
        </div>
      </div>

      {isDropdownOpen && (
        <button
          type="button"
          aria-label="Fechar dropdown de busca"
          className="fixed left-0 top-0 z-[100] h-screen w-screen backdrop-brightness-75 cursor-default"
          onClick={onClose}
        />
      )}
    </>
  );
};
